/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { useMemo } from '@safe-wordpress/element';
import { applyFilters } from '@safe-wordpress/hooks';

/**
 * External dependencies
 */
import { isPremium } from '@nelio/forms/utils';
import type {
	ActionTypeDefinition,
	ActionType,
	PremiumEditComponent,
} from '@nelio/forms/types';

/**
 * Internal dependencies
 */
import cookieCreation from './cookie-creation';
import emailNotification from './email-notification';
import postCreation from './post-creation';
import userRegistration from './user-registration';
import mailchimpIntegration from './mailchimp-integration';
import webhookIntegration from './webhook-integration';
import zapierIntegration from './zapier-integration';

const actionTypes: ReadonlyArray< ActionTypeDefinition > = [
	cookieCreation,
	emailNotification as ActionTypeDefinition,
	mailchimpIntegration,
	postCreation,
	userRegistration,
	webhookIntegration,
	zapierIntegration,
];

export const useActionTypes = (): ReadonlyArray< ActionType< unknown > > => {
	const disabledActionTypes = useSelect(
		( select ) =>
			actionTypes
				.map( ( { canCurrentUserUse, isPremium: isPremiumAction } ) =>
					( isPremiumAction && ! isPremium() ) ||
					( !! canCurrentUserUse &&
						// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
						! canCurrentUserUse( select as any ) )
						? '1'
						: '0'
				)
				.join( '' ),
		[]
	);

	return useMemo(
		() =>
			actionTypes.map(
				(
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					{ canCurrentUserUse, ...at },
					index
				): ActionType< unknown > => {
					const isDisabled = disabledActionTypes[ index ] === '1';
					if ( at.isPremium ) {
						const edit = applyFilters(
							`nelio_forms.get_action_${ at.type }_edit_component`,
							() => null
						) as PremiumEditComponent< unknown >;
						const defaults = applyFilters(
							`nelio_forms.get_action_${ at.type }_defaults`,
							{}
						);
						return {
							...at,
							edit,
							isDisabled,
							defaults,
						};
					} //end if
					return { ...at, isDisabled };
				}
			),
		[ disabledActionTypes ]
	);
};

export const useActionType = < T >(
	type: string
): ActionType< T > | undefined =>
	useActionTypes().find( ( a ) => a.type === type ) as ActionType< T >;
